<script lang="ts" setup>
import type { BlockPropCustomComponentProps } from '#layers/block-engine/public';
import { useI18n } from 'vue-i18n';
import { useBlockInEditorContext } from '#layers/block-engine/public';
import { getPrimitiveFieldInputBlockOptions, getSingleRelationFieldInputBlockOptions, useWorkspaceSchemaStore } from '#layers/data-model/public';
import { ButtonDestructive, showConfirmationDialog } from '#layers/ui/public';

defineProps<BlockPropCustomComponentProps>();

/**
 * Ejected
 */
const model = defineModel<boolean>();

const { t } = useI18n();

const { insertBlock, getSlot, getProps } = useBlockInEditorContext();
const { findPrimitiveFieldById, findSingleRelationFieldById } = useWorkspaceSchemaStore();

async function insertFormField() {
	const children = getSlot('default');

	if (children.length > 0) {
		return;
	}

	const { fieldId } = getProps();

	if (!fieldId) {
		return;
	}

	const primitiveField = findPrimitiveFieldById(fieldId);
	const relationField = findSingleRelationFieldById(fieldId);

	if (primitiveField) {
		await insertBlock(getPrimitiveFieldInputBlockOptions(primitiveField));
	}
	else if (relationField) {
		await insertBlock(getSingleRelationFieldInputBlockOptions(relationField));
	}
}

async function eject() {
	if (!await showConfirmationDialog({
		props: {
			title: t('blocks.form_field.eject_confirm.title'),
			description: t('blocks.form_field.eject_confirm.description'),
			confirm: {
				label: t('blocks.form_field.eject_confirm.confirm'),
				color: 'warning',
			},
		},
	},
	)) {
		return;
	}

	model.value = true;

	await insertFormField();
}

async function cancelEject() {
	if (!await showConfirmationDialog({
		props: {
			title: t('blocks.form_field.cancel_eject_confirm.title'),
			description: t('blocks.form_field.cancel_eject_confirm.description'),
			confirm: {
				label: t('blocks.form_field.cancel_eject_confirm.confirm'),
				color: 'error',
			},
		},
	})) {
		return;
	}

	model.value = false;

	// TODO this needs to actually remove the children from the block, otherwise it isn't really a reset
}
</script>

<template>
	<DButton
		v-if="!model"
		:label="$t('blocks.form.eject')"
		icon="lucide:unplug"
		size="sm"
		block
		@click="eject()"
	/>
	<ButtonDestructive
		v-else
		:label="$t('blocks.form.cancel_eject')"
		icon="lucide:x"
		size="sm"
		variant="outline"
		block
		@click="cancelEject()"
	/>
</template>
